import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabaseClient } from "./supabaseClient";

/**
 * Correct teardown for a Supabase realtime channel.
 *
 * WHY (2026-08-12 audit): 20 of our 25 channel teardowns called
 * `channel.unsubscribe()`. That sends `phx_leave` but leaves the channel in the
 * client's internal registry, so the client still holds a reference to a dead
 * channel on that topic. `supabase.removeChannel()` unsubscribes AND removes it.
 *
 * Two reasons this matters:
 *
 *  1. It leaks. Every mount/unmount of a dialer, lead panel or chat view adds another
 *     stale entry.
 *  2. Re-joining the SAME topic afterwards is the exact pattern that newer realtime-js
 *     versions broke on ("no way to subscribe after unsubscribe", introduced with the
 *     phoenix rewrite in supabase-js 2.100 and still unfixed at 2.112.3). We are pinned
 *     at 2.99.2 so it does not bite today, but every one of these sites is a landmine
 *     for the day we upgrade.
 *
 * Most teardowns run in a React cleanup where the Supabase client is not in scope, which
 * is why this resolves the client itself rather than taking one as an argument.
 *
 * Fire-and-forget and never throws: teardown failing must not break an unmount.
 */
export function removeRealtimeChannel(channel: RealtimeChannel | null | undefined): void {
  if (!channel) return;
  void getSupabaseClient()
    .then((supabase) => supabase?.removeChannel(channel))
    .catch(() => {
      // Last resort — if the client can't be resolved, at least send phx_leave.
      try {
        void channel.unsubscribe();
      } catch {
        /* noop */
      }
    });
}
